<template>
  <div :class="[s.wrap, s[helper.displayDirection]]">
    <div v-if="user.login.isChecked.isLogin && user.login.isLogin" :class="s.content">

      <!-- demandArea選択（複数エリアの場合のみ表示） -->
      <div v-if="demandAreas.length > 1" :class="s.select_block">
        <div :class="s.label">エリアを選択してください</div>
        <select
          class="form-control"
          name="demandArea" id="demandArea"
          v-model="selectedDemandAreaId">
          <option :value="null">選択してください</option>
          <option
            v-for="area in demandAreas" :key="area.id"
            :value="area.id">{{ area.label }}</option>
        </select>
      </div>

      <!-- demandSchedule選択 -->
      <div v-if="selectedDemandAreaId" :class="s.select_block">
        <div :class="s.label">スケジュールを選択してください</div>
        <div v-if="flag.isLoading.schedules"><i class="fa-solid fa-spinner fa-spin" /></div>
        <div v-else-if="!demandSchedules.length" :class="s.empty">明日以降のスケジュールがありません。</div>
        <select
          v-else
          class="form-control"
          name="demandSchedule" id="demandSchedule"
          v-model="selectedScheduleId">
          <option :value="null">選択してください</option>
          <option
            v-for="sche in demandSchedules" :key="sche.id"
            :value="sche.id">{{ scheduleLabel(sche) }}</option>
        </select>
      </div>

      <!-- 予約一覧 -->
      <div v-if="selectedScheduleId" :class="s.reservations">
        <div v-if="flag.isLoading.reservations"><i class="fa-solid fa-spinner fa-spin" /></div>
        <div v-else-if="!demandCustomers.length" :class="s.empty">このスケジュールの予約はありません。</div>
        <div v-else>
          <div :class="s.count">配車 {{ reservations.length }}件</div>
          <div v-for="(rsv, i) in reservations" :key="rsv.id" :class="[i !== 0 && 'mt-2']">
            <div :class="s.locations">
              <div
                v-for="(location, i) in rsv.locations" :key="`${location.time}-${i}`"
                :class="s.point">
                <span :class="[s.time, i === 0 ? s.emphasis : '']">{{ pointTime(location.time) }}</span>
                <span :class="[s.badge, location.type === 'pickup' ? s.ride : s.drop]">{{ location.type === 'pickup' ? '乗' : '降' }}</span>
                <span :class="s.title">{{ location.title || location.address || '--' }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex';
import cf from '@/mixins/commonFunctions.js';

export default {
  name: 'DemandReservationSearch',
  mixins: [cf],
  data() {
    return {
      flag: {
        isLoading: {
          schedules: false,
          reservations: false,
        },
      },
      selectedDemandAreaId: null,
      selectedScheduleId: null,
      demandSchedules: [],
      demandCustomers: [],
      reservations: [],
    };
  },
  computed: {
    ...mapState(['user', 'helper']),
    demandAreas() {
      if (this.user.driverDemandAreas?.length) return this.user.driverDemandAreas;
      return this.user.offices?.map((o) => o.demandAreas || []).flat() || [];
    },
  },
  watch: {
    demandAreas: {
      handler(areas) {
        // エリアが1つの場合は自動選択
        if (areas.length === 1 && !this.selectedDemandAreaId) {
          this.selectedDemandAreaId = areas[0].id;
          this.getDemandSchedules();
        }
      },
      immediate: true,
    },
    selectedDemandAreaId() {
      this.selectedScheduleId = null;
      this.demandSchedules = [];
      this.demandCustomers = [];
      if (this.selectedDemandAreaId) this.getDemandSchedules();
    },
    selectedScheduleId() {
      this.demandCustomers = [];
      this.reservations = [];
      if (this.selectedScheduleId) this.getReservations();
    },
  },
  methods: {
    /** 明日以降のdemandScheduleを取得 */
    getDemandSchedules() {
      this.flag.isLoading.schedules = true;
      const params = {
        demandAreaIds: [this.selectedDemandAreaId],
        isFuture: 22, // 明日以降（当日含まない）
        orderBy: 'start',
        order: 'asc',
      };
      this.axios({
        method: 'GET',
        url: '/v1/demandSchedule/get/list',
        params,
      })
        .then((response) => {
          this.demandSchedules = response.data.demandSchedules.data;
        })
        .catch((error) => {
          if (error.response) console.log(error.response.data);
          else console.log(error);
          alert('スケジュールの取得に失敗しました。再度お試しください。');
        })
        .finally(() => {
          this.flag.isLoading.schedules = false;
        });
    },

    /** 選択したdemandScheduleに紐付く予約を取得 */
    getReservations() {
      this.flag.isLoading.reservations = true;
      const params = {
        ids: [this.selectedScheduleId],
        withReservations: 1, // 有効な予約のみ
        withReservationLandmarks: 1,
      };
      this.axios({
        method: 'GET',
        url: '/v1/demandSchedule/get/list',
        params,
      })
        .then((response) => {
          const schedule = response.data.demandSchedules.data[0];
          const rsvs = schedule?.reservations || [];
          this.demandCustomers = rsvs.map((rsv) => rsv.demandCustomers || []).flat();
          // 乗降地点をp_datetime/d_datetime基準の乗降順に整形
          this.reservations = rsvs.map((r) => this.formatDemandReservation(r, []));
        })
        .catch((error) => {
          if (error.response) console.log(error.response.data);
          else console.log(error);
          alert('予約の取得に失敗しました。再度お試しください。');
        })
        .finally(() => {
          this.flag.isLoading.reservations = false;
        });
    },

    scheduleLabel(sche) {
      const weekdays = ['日', '月', '火', '水', '木', '金', '土'];
      const date = this.formatTimestamp(sche.start, 'MM/DD');
      const weekday = weekdays[new Date(sche.start).getDay()];
      const start = this.formatTimestamp(sche.start, 'HH:mm');
      const end = this.formatTimestamp(sche.end, 'HH:mm');
      return `${date}(${weekday}) ${sche.label} ${start}~${end}`;
    },

    pointTime(datetime) {
      return datetime ? this.formatTimestamp(datetime, 'HH:mm') : '--:--';
    },
  },
};
</script>

<style lang="scss" module="s">
.wrap {
  height: 100%;
}

.content {
  padding: 12px;
}

.select_block {
  margin-bottom: 16px;
  .label {
    font-weight: bold;
    margin-bottom: 4px;
  }
}

.empty {
  padding: 8px 0;
}

.reservations {
  margin-top: 8px;
  .count {
    font-weight: bold;
    margin-bottom: 8px;
  }
}

.locations {
  border: 1px solid var(--border-gray);
  border-radius: 6px;
  padding: 8px 12px;
  background-color: #ffffff;
}

.point {
  display: flex;
  align-items: center;
  &:not(:first-child) {
    margin-top: 4px;
  }
  .time {
    width: 72px;
    flex-shrink: 0;
    color: var(--bs-gray);
    &.emphasis {
      font-weight: bold;
      color: var(--primary-blue);
    }
  }
  .badge {
    flex-shrink: 0;
    font-size: 16px;
    font-weight: bold;
    color: #ffffff;
    border-radius: 4px;
    padding: 2px 6px;
    margin-right: 8px;
    &.ride {
      background-color: var(--bs-gray);
    }
    &.drop {
      background-color: var(--bs-gray);
    }
  }
  .title {
    word-break: break-all;
  }
}
</style>
